import { useEffect, useRef, type RefObject } from "react";

/**
 * Foco, teclado e rolagem dos overlays modais.
 *
 * Esta é a ÚNICA fonte desse comportamento. Antes dela cada modal e cada drawer
 * registrava o próprio listener de `Escape` no documento, o que produzia um
 * defeito real: com dois overlays abertos, uma tecla fechava os dois. O
 * `AGENTS.md` §14.2 exige o contrário — `Escape` fecha o overlay SUPERIOR — e
 * §17 exige foco preso, foco inicial previsível e retorno ao gatilho.
 *
 * O que este hook garante, para todo overlay que declara `aria-modal="true"`:
 *
 * 1. **Pilha determinística.** Só o overlay do topo responde a `Escape` e só
 *    ele prende o foco. Fechar o de cima devolve o comando ao de baixo.
 * 2. **Foco inicial previsível.** O foco que o próprio conteúdo já definiu por
 *    `autoFocus` é respeitado; sem ele, entra o alvo explícito, depois o
 *    primeiro focável, e por fim o próprio container. Nenhum overlay começa
 *    com o foco perdido no documento.
 * 3. **Foco preso de verdade.** `Tab` e `Shift+Tab` circulam dentro do
 *    overlay, e foco que escape por clique ou por chamada programática é
 *    trazido de volta. Um trap que só cobre `Tab` não é trap.
 * 4. **Retorno ao gatilho.** Ao fechar, o foco volta para o elemento que abriu
 *    o overlay, quando ele ainda existe no documento.
 * 5. **Rolagem do fundo bloqueada** enquanto houver qualquer overlay aberto, e
 *    liberada apenas quando o último fechar.
 *
 * O que ele NÃO faz, de propósito: não decide se o clique no backdrop fecha.
 * Essa regra depende de haver alteração não salva (§14.2) e pertence a cada
 * superfície, que conhece o próprio estado sujo.
 */

/** Pilha dos overlays modais abertos. O último elemento é o topo. */
const openOverlays: symbol[] = [];

function isTopmost(token: symbol): boolean {
  return openOverlays[openOverlays.length - 1] === token;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
  "[contenteditable='true']",
].join(",");

/**
 * Elementos focáveis do container, na ordem do documento.
 *
 * A visibilidade NÃO é medida por layout: `offsetParent` e `getClientRects()`
 * são sempre vazios no jsdom, e filtrar por eles deixaria o trap invisível
 * exatamente para os testes que precisam prová-lo. O filtro usa atributos, que
 * significam a mesma coisa nos dois ambientes.
 */
export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      !element.hasAttribute("hidden") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getAttribute("type") !== "hidden" &&
      element.tabIndex !== -1,
  );
}

let scrollLocks = 0;
let previousOverflow: string | null = null;

function lockDocumentScroll() {
  scrollLocks += 1;
  if (scrollLocks > 1) return;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  // O céu do fundo lê esta marca para parar enquanto há modal aberto: atrás
  // do véu, cada quadro das estrelas obrigaria o navegador a refazer o
  // desfoque da tela inteira.
  document.documentElement.dataset.modal = "open";
}

function releaseDocumentScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks > 0) return;
  document.body.style.overflow = previousOverflow ?? "";
  previousOverflow = null;
  delete document.documentElement.dataset.modal;
}

export interface ModalOverlayOptions {
  /** Elemento do diálogo; o foco fica preso dentro dele. */
  containerRef: RefObject<HTMLElement | null>;
  /** Pedido de fechamento pelo teclado. Só o overlay do TOPO é notificado. */
  onClose: () => void;
  /** Foco inicial explícito, quando o primeiro focável não é o alvo certo. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Falso desliga tudo, para overlay que permanece montado enquanto fechado. */
  active?: boolean;
}

export function useModalOverlay({
  containerRef,
  onClose,
  initialFocusRef,
  active = true,
}: ModalOverlayOptions): void {
  // O efeito não pode depender da identidade de `onClose`: um handler recriado
  // a cada render remontaria o overlay, perdendo gatilho e foco inicial.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const explicitFocusRef = useRef(initialFocusRef);
  explicitFocusRef.current = initialFocusRef;

  // O `autoFocus` do React roda durante o commit, antes deste efeito. Capturar o
  // gatilho apenas dentro do efeito faria o próprio campo do diálogo parecer o
  // elemento de origem e, ao fechar, o foco cairia no body. A transição para
  // `active` ainda acontece durante o render, quando o gatilho real continua
  // focado. O ref preserva esse elemento sem provocar novo render.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasActiveRef = useRef(false);
  if (active && !wasActiveRef.current) {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
  }
  wasActiveRef.current = active;

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const token = Symbol("overlay");
    openOverlays.push(token);
    lockDocumentScroll();

    const trigger = returnFocusRef.current;

    if (!container.contains(document.activeElement)) {
      const explicit = explicitFocusRef.current?.current ?? null;
      const target = explicit ?? focusableWithin(container)[0] ?? null;
      if (target) target.focus();
      else {
        container.setAttribute("tabindex", "-1");
        container.focus();
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmost(token)) return;
      if (event.key === "Escape") {
        // Impede que o overlay de baixo, ou um atalho de página, também trate
        // a mesma tecla: só o topo fecha.
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement as HTMLElement | null;
      const outside = !container.contains(current);
      if (event.shiftKey && (current === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isTopmost(token)) return;
      const target = event.target as Node | null;
      if (target && container.contains(target)) return;
      (focusableWithin(container)[0] ?? container).focus();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("focusin", handleFocusIn, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("focusin", handleFocusIn, true);
      const index = openOverlays.indexOf(token);
      if (index >= 0) openOverlays.splice(index, 1);
      releaseDocumentScroll();
      if (trigger?.isConnected && typeof trigger.focus === "function") trigger.focus();
    };
  }, [active, containerRef]);
}

/** Somente para teste: a pilha precisa ser observável para provar o empilhamento. */
export function openOverlayCountForTests(): number {
  return openOverlays.length;
}
